import React, { memo, useEffect, useCallback, useState, useRef, useContext } from 'react';
import type { FC } from 'react';

import type { InlineCommentViewComponentProps } from '@atlaskit/editor-common/types';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import type { AnnotationInfo } from '@atlaskit/editor-plugins/annotation';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/comments-data-manager/entry-points/getCommentsDataManagerExperimentAttributes';

import { InlineComment } from '../InlineComment';

const relativeFilePath = 'annotation-provider-inline-comments/renderer/ViewComponent';

export type ViewComponentProps = {
	pageId: string;
	updateDocument?: (doc: JSONDocNode) => void;
	isArchived?: boolean;
	wasHighlightClicked?: boolean;
	pageContainsBodiedMacros?: boolean;
	isOffline?: boolean;
};

export const rendererUpdateEvent = (
	eventEmitter: AnnotationUpdateEmitter,
	newSelection?: undefined | string,
) => {
	if (newSelection) {
		eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
			annotationId: newSelection,
		});
	} else {
		eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
		eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
	}
};

export const ViewComponent: FC<InlineCommentViewComponentProps & ViewComponentProps> = memo(
	({
		annotations: clickedAnnotations,
		getInlineNodeTypes,
		deleteAnnotation,
		pageId,
		updateDocument,
		isArchived,
		isOffline,
		wasHighlightClicked,
		pageContainsBodiedMacros,
	}) => {
		const viewInlineCommentPressTrace = usePressTracing('view-inline-comment-action');

		const { documentUpdated } = useDocumentUpdateStatus();
		const { storedCommentData } = useAnnotations();
		const { clearStoredCommentData } = useAnnotationsDispatch();
		const [selectedAnnotation, setSelectedAnnotation] = useState<AnnotationInfo | null | undefined>(
			null,
		);
		const [eventEmitter] = useState(getRendererAnnotationEventEmitter());
		const prevPageIdRef = useRef(pageId);
		const { removeCommentQueryParams } = useInlineCommentQueryParams();
		const { hasContentChanged } = useCommentsContentState();
		const { resetContentChanged } = useCommentsContentActions();
		const [isInlineCommentVisible, setIsInlineCommentVisible] = useState<boolean>(false);

		const inlineCommentRef = useCallback((node: any) => {
			if (node === null) {
				setIsInlineCommentVisible(false);
			} else {
				setIsInlineCommentVisible(true);
			}
		}, []);

		const { showModal } = useDialogs();
		const { setCurrentlySelectedThreadKey, setCurrentView } = useCommentsPanelActions();
		const { currentView } = useCommentsPanelState();
		const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
		const { isCommentsPanelShown } = useShowCommentsPanel();
		const experienceTracker = useContext(ExperienceTrackerContext);

		const supportedActions: CommentAction[] =
			pageContainsBodiedMacros && !fg('confluence_comment_nested_renderer_delete')
				? ['edit', 'resolve']
				: ['edit', 'resolve', 'delete'];

		const handleAnnotationClick = useCallback(
			(annotation?: AnnotationInfo) => {
				// If the current annotation has been clicked again
				if (selectedAnnotation && annotation && selectedAnnotation.id === annotation.id) {
					// Send an event to tell the Renderer to de-select the annotation
					rendererUpdateEvent(eventEmitter);
					setSelectedAnnotation(null);

					if (isCommentsPanelShown && fg('confluence-frontend-comments-panel')) {
						setCurrentlySelectedThreadKey('');
					}

					if (fg('confluence_frontend_view_inline_comment_renderer')) {
						experienceTracker.abort({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							reason: 'Current annotation was clicked again. Annotation was deselected',
							checkForTimeout: true,
							publishDebugPoints: true,
							attributes: {
								stopPoint: relativeFilePath,
							},
						});
					}
				} else {
					viewInlineCommentPressTrace();

					// Only start the experience if the comments panel is closed to track the core view-inline-comment experience
					if (
						!isCommentsPanelShown &&
						annotation?.id &&
						fg('confluence_frontend_view_inline_comment_renderer')
					) {
						const experienceState = experienceTracker.getExperienceState(
							VIEW_INLINE_COMMENT_EXPERIENCE,
						);

						/*
						 * Note: This logic runs after certain entrypoints start the view-inline-comment experience.
						 * We don't start a new experience if the annotation ID of the running experience is the same
						 * as the current annotation to avoid duplicating and aborting experiences.
						 */
						if (
							experienceState === null ||
							experienceState?.hasStopped ||
							(experienceState?.hasStopped === false &&
								experienceState?.attributes?.annotationId !== annotation?.id)
						) {
							experienceTracker.start({
								name: VIEW_INLINE_COMMENT_EXPERIENCE,
								timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
								attributes: {
									...getCommentsDataManagerExperimentAttributes(),
									annotationId: annotation?.id,
									entryPoint: relativeFilePath,
									pageClass: 'renderer',
								},
							});
						}
					}

					// Send an event to tell the renderer to select the annotation
					rendererUpdateEvent(eventEmitter, annotation?.id);
					// Select it in our code
					setSelectedAnnotation(annotation);

					const shouldSetSelectedCommentMarkerRef = isCommentsPanelShown && annotation;
					if (shouldSetSelectedCommentMarkerRef && fg('confluence-frontend-comments-panel')) {
						setCurrentlySelectedThreadKey(annotation.id);
						let expectedView = undefined;
						if (
							(currentView === ViewValues.RESOLVED || currentView === ViewValues.DELETED_CONTENT) &&
							fg('confluence_comments_unified_navigation_viewchange')
						) {
							expectedView = ViewValues.OPEN;
							setCurrentView && setCurrentView(ViewValues.OPEN);
						}
						if (fg('confluence_comments_unified_navigation')) {
							handleCommentsPanelNavigationWithViewChange({
								scrollBehavior: ScrollBehavior.SMOOTH,
								context: NavigationContext.PANEL,
								block: 'center',
								annotationId: annotation.id,
								annotationClicked: true,
								expectedView,
							});
						} else {
							scrollCommentsPanelToThread({
								annotationId: annotation.id,
								annotationClicked: true,
							});
						}
					}
				}
			},
			[
				eventEmitter,
				selectedAnnotation,
				isCommentsPanelShown,
				setSelectedAnnotation,
				setCurrentlySelectedThreadKey,
				scrollCommentsPanelToThread,
				experienceTracker,
				viewInlineCommentPressTrace,
				setCurrentView,
				currentView,
			],
		);

		const onNavigationClick = useCallback(
			(nextMarkerRef: string) => {
				if (fg('confluence_frontend_view_inline_comment_arrow_nav')) {
					experienceTracker.updateAttributes({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						attributes: {
							annotationId: nextMarkerRef,
						},
					});
				}

				const doNavigation = async () => {
					handleAnnotationClick({
						id: nextMarkerRef,
						type: AnnotationTypes.INLINE_COMMENT,
					});
				};

				if (hasContentChanged) {
					let abortedCommentNavigationExperience = false;

					if (fg('confluence_frontend_view_inline_comment_arrow_nav')) {
						const experienceState = experienceTracker.getExperienceState(
							VIEW_INLINE_COMMENT_EXPERIENCE,
						);

						/*
						 * If the comment navigation is used to navigate when the content has changed, we abort and restart
						 * the experience on confirm because we can't predict if the user will confirm the navigation, or
						 * how long the user will take to confirm.
						 */
						if (
							experienceState !== null &&
							experienceState?.hasStopped === false &&
							experienceState?.attributes?.entryPoint === 'inline-comments-common/CommentNavigation'
						) {
							experienceTracker.abort({
								name: VIEW_INLINE_COMMENT_EXPERIENCE,
								reason: 'Comment warning dialog displayed',
								checkForTimeout: true,
								publishDebugPoints: true,
								attributes: {
									stopPoint: relativeFilePath,
								},
							});

							abortedCommentNavigationExperience = true;
						}
					}

					showModal(CommentWarningDialog, {
						onConfirm: () => {
							if (abortedCommentNavigationExperience) {
								experienceTracker.start({
									name: VIEW_INLINE_COMMENT_EXPERIENCE,
									timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
									attributes: {
										...getCommentsDataManagerExperimentAttributes(),
										annotationId: nextMarkerRef,
										entryPoint: relativeFilePath,
										pageClass: 'renderer',
									},
								});

								experienceTracker.attachDebugPoint({
									name: VIEW_INLINE_COMMENT_EXPERIENCE,
									message:
										'Restarting experience after comment was discarded from comment warning dialog. Initial entrypoint: inline-comments-common/CommentNavigation',
								});
							}

							resetContentChanged();
							void doNavigation();
						},
					});
				} else {
					void doNavigation();
				}
			},
			[hasContentChanged, handleAnnotationClick, resetContentChanged, showModal, experienceTracker],
		);

		useEffect(() => {
			if (!clickedAnnotations.length && !selectedAnnotation) {
				setSelectedAnnotation(null);
				eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);

				if (fg('confluence_frontend_view_inline_comment_renderer')) {
					/*
					 * Handle the case where the annotation is quickly deselected before the
					 * renderer has a chance to render the inline comment. This can happen when
					 * the user double clicks on an annotation, the user navigates via keyboard
					 * shortcut to a general comment, or the inline comment is unmounted.
					 */
					experienceTracker.abort({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						reason: 'No annotation selected. Annotation was likely deselected.',
						checkForTimeout: true,
						publishDebugPoints: true,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}

				return;
			}

			// Multi-selection to be implemented in future
			const newlySelectedAnnotation = clickedAnnotations[0];

			if (documentUpdated && !wasHighlightClicked) {
				if (selectedAnnotation?.id === newlySelectedAnnotation?.id) {
					return;
				}
			}

			handleAnnotationClick(newlySelectedAnnotation);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [clickedAnnotations]);

		useEffect(() => {
			const prevPageId = prevPageIdRef.current;

			// Whenever the pageId changes, we need to close anything that's open
			if (pageId !== prevPageId) {
				handleOnClose();
			}

			return () => {
				if (pageId !== prevPageId) {
					handleOnClose();
				}
			};
			// We only want to listen to the pageId changing
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [pageId]);

		const handleOnClose = useCallback(() => {
			// Inform the Renderer to de-select the annotation
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			// Remove the display of the comment
			setSelectedAnnotation(null);
			removeCommentQueryParams();

			if (storedCommentData) {
				clearStoredCommentData();
			}

			if (fg('confluence_frontend_view_inline_comment_renderer')) {
				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: 'Inline comment was closed',
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			}
		}, [
			eventEmitter,
			setSelectedAnnotation,
			removeCommentQueryParams,
			experienceTracker,
			storedCommentData,
			clearStoredCommentData,
		]);

		const onDeleteSuccess = (doc: JSONDocNode) => {
			if (doc) {
				// Inform the Renderer to de-select the annotation
				eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
				eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
				updateDocument && updateDocument(doc);
			}
		};

		const shouldRenderStandaloneInlineComment =
			selectedAnnotation && (!fg('confluence-frontend-comments-panel') || !isCommentsPanelShown);

		return shouldRenderStandaloneInlineComment ? (
			<InlineComment
				annotation={selectedAnnotation}
				getInlineNodeTypes={getInlineNodeTypes}
				isArchived={isArchived}
				onClose={handleOnClose}
				onResolve={() => {}}
				deleteOptions={{
					deleteAnnotation,
					onDeleteSuccess,
				}}
				onNavigationClick={onNavigationClick}
				supportedTopLevelActions={supportedActions}
				inlineCommentRef={inlineCommentRef}
				isInlineCommentVisible={isInlineCommentVisible}
				isOffline={isOffline}
				key={`inline-comment-${selectedAnnotation?.id}`}
			/>
		) : null;
	},
);
